import type { PageTab } from "@agent-ui/features/page-renderer/store/pageTabsStore";
import type { OpencodeMessagePart } from "schemas/generated/opencode";
import type { OpencodeEvent } from "schemas/opencode";

import { useCallback, useEffect, useRef } from "react";

import { useSessionUuid } from "@agent-ui/features/chat/hooks/useSessionUuid";
import {
  useSessionStoreActions,
  useSessionStoreQueries,
} from "@agent-ui/features/chat/store/SessionStoreContext";
import { extractPageRouteFromToolPart } from "@agent-ui/features/chat/utils/pageRouteExtraction";
import { usePageTabsActions } from "@agent-ui/features/page-renderer/store/pageTabsStore";
import {
  useSandboxNavigate,
  useSetChatPreviewUrl,
} from "@agent-ui/store/sandboxNavigationStore";
import { useReploAgent } from "@v2/contexts/ReploAgentContext";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { opencodeUtils } from "@v2/trpc/routes/opencode";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { isDefaultOpencodeSessionTitle } from "replo-utils/lib/opencode";
import {
  getSessionIdFromOpencodeEvent,
  opencodePersistenceEventSchema,
  questionAskedEventSchema,
  sessionUpdatedEventSchema,
} from "schemas/opencode";
import {
  isPersistenceEventType,
  transformUpstreamToOpencodeMessage,
  transformUpstreamToOpencodeMessagePart,
} from "schemas/utils/opencode";

const SESSION_END_STATES = new Set([
  "idle",
  "complete",
  "completed",
  "finished",
  "paused",
  "cancelled",
  "error",
]);

const SESSION_END_EVENT_TYPES = new Set([
  "session.idle",
  "session.complete",
  "session.finished",
  "session.paused",
  "session.cancelled",
  "session.error",
]);

const SUBAGENT_SPAWN_GRACE_MS = 1500;

function isSessionEndEvent(event: OpencodeEvent): boolean {
  if (SESSION_END_EVENT_TYPES.has(event.type)) {
    return true;
  }

  if (event.type === "session.updated") {
    const sessionState =
      (event.properties as { state?: string }).state?.toLowerCase() ?? "";
    return SESSION_END_STATES.has(sessionState);
  }

  return false;
}

function handleToolNavigationIfNeeded({
  part,
  sandboxNavigate,
  openTab,
}: {
  part: OpencodeMessagePart;
  sandboxNavigate: ReturnType<typeof useSandboxNavigate>;
  openTab: (tab: PageTab) => void;
}) {
  const extractedPageRoute = extractPageRouteFromToolPart(part);
  if (!extractedPageRoute) {
    return;
  }

  const { route, toolAction } = extractedPageRoute;
  sandboxNavigate(route);
  openTab({
    id: route,
    content: {
      type: "page",
      pageRoute: route,
      status: toolAction === "write" ? "new" : "edited",
    },
  });
}

/**
 * Use once in the top-level chat shell while the agent experience is mounted.
 * It subscribes to OpenCode events, routes them into SessionStore by `sessionUuid`,
 * marks streaming as ended on terminal session events, and syncs preview URL state.
 */
export function useOpencodeEventRouter() {
  const {
    addEvent,
    addOrUpdateMessage,
    addOrUpdateMessagePart,
    setStreaming,
    setQuestionId,
    getSessionUuidByAgentSessionId,
    getMessageByOpencodeId,
    applyTextDelta,
    registerSubagentAgentSessionId,
    registerSubagentSession,
    completeSubagentSession,
    setToolCallSubagentSession,
  } = useSessionStoreActions();
  const { isSubagentSession, findRunningTaskToolCallId, hasActiveSubagents } =
    useSessionStoreQueries();
  const currentSessionUuid = useSessionUuid();
  const setChatPreviewUrl = useSetChatPreviewUrl();
  const sandboxNavigate = useSandboxNavigate();
  const { user } = useCurrentUser();
  const { openTab } = usePageTabsActions();

  // NOTE (Berkley, 2026-03-05): When the parent session goes idle, we delay
  // closing the UI for a short window to allow post-idle subagents (e.g. the
  // commit-enforcer) to spawn. If a subagent appears within the window we stay
  // open until all subagents complete. If none appears, we close after the delay.
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const parentIdleSessionRef = useRef<{
    sessionUuid: string;
    agentSessionId: string;
  } | null>(null);

  const closeParentSession = useCallback(
    ({ sessionUuid }: { sessionUuid: string }) => {
      setStreaming({ sessionUuid, isStreaming: false });
      void opencodeUtils.getPaginatedSessions.invalidate();
      parentIdleSessionRef.current = null;
    },
    [setStreaming],
  );

  const handleEvent = useCallback(
    (event: OpencodeEvent) => {
      const agentSessionId = getSessionIdFromOpencodeEvent(event);
      if (!agentSessionId) {
        return;
      }

      // Handle subagent session creation before the unregistered-session guard.
      // The subagent's agentSessionId won't be registered yet, so we register
      // it here by mapping it to the parent's sessionUuid.
      if (event.type === "session.created") {
        const { parentID } = event.properties.info;
        if (parentID) {
          const parentSessionUuid = getSessionUuidByAgentSessionId({
            agentSessionId: parentID,
          });
          if (parentSessionUuid) {
            registerSubagentAgentSessionId({
              parentSessionUuid,
              agentSessionId,
            });
            registerSubagentSession({
              parentSessionUuid,
              subagentAgentSessionId: agentSessionId,
            });
            const toolCallId = findRunningTaskToolCallId({
              sessionUuid: parentSessionUuid,
            });
            if (toolCallId) {
              setToolCallSubagentSession({
                toolCallId,
                agentSessionId,
              });
            }
            if (
              idleTimerRef.current &&
              parentIdleSessionRef.current?.agentSessionId === parentID
            ) {
              clearTimeout(idleTimerRef.current);
              idleTimerRef.current = null;
            }
          }
        }
        return;
      }

      const sessionUuid = getSessionUuidByAgentSessionId({ agentSessionId });
      if (!sessionUuid) {
        return;
      }

      if (event.type === "message.part.delta") {
        if (event.properties.field === "text") {
          const { messageID, partID, delta } = event.properties;
          applyTextDelta({
            sessionUuid,
            messageOpencodeId: messageID,
            partOpencodeId: partID,
            delta,
          });
        }
        return;
      }

      addEvent({ sessionUuid, event });

      if (isSessionEndEvent(event)) {
        if (isSubagentSession({ agentSessionId })) {
          completeSubagentSession({ agentSessionId });
          if (
            parentIdleSessionRef.current?.sessionUuid === sessionUuid &&
            !hasActiveSubagents({ sessionUuid })
          ) {
            if (idleTimerRef.current) {
              clearTimeout(idleTimerRef.current);
              idleTimerRef.current = null;
            }
            closeParentSession({ sessionUuid });
          }
        } else {
          parentIdleSessionRef.current = { sessionUuid, agentSessionId };
          if (idleTimerRef.current) {
            clearTimeout(idleTimerRef.current);
          }
          idleTimerRef.current = setTimeout(() => {
            idleTimerRef.current = null;
            if (!hasActiveSubagents({ sessionUuid })) {
              closeParentSession({ sessionUuid });
            }
          }, SUBAGENT_SPAWN_GRACE_MS);
        }
      } else if (event.type === "session.updated") {
        const parsed = sessionUpdatedEventSchema.parse(event);
        const { title } = parsed.properties.info;
        if (title && !isDefaultOpencodeSessionTitle(title)) {
          // NOTE (Max, 2026-02-26): Invalidating these so that the ChatHeader
          // and the sidebar pick up the new title.
          void opencodeUtils.getPaginatedSessions.invalidate();
          void opencodeUtils.getSession.invalidate({ sessionUuid });
        }
      } else if (event.type === "question.asked") {
        const parsed = questionAskedEventSchema.parse(event);
        const { id: questionId, tool } = parsed.properties;
        const toolCallId = tool.callID;

        setQuestionId({ toolCallId, questionId });
      } else if (isPersistenceEventType(event.type) && user?.id) {
        const parsedPersistenceEvent =
          opencodePersistenceEventSchema.safeParse(event);

        if (parsedPersistenceEvent.success) {
          exhaustiveSwitch(parsedPersistenceEvent.data)({
            "message.created": (parsedEvent) => {
              const message = transformUpstreamToOpencodeMessage({
                upstreamMessage: parsedEvent.properties.info,
                userId: user.id,
                opencodeSessionUuid: sessionUuid,
              });

              addOrUpdateMessage({ sessionUuid, message });
            },
            "message.updated": (parsedEvent) => {
              const message = transformUpstreamToOpencodeMessage({
                upstreamMessage: parsedEvent.properties.info,
                userId: user.id,
                opencodeSessionUuid: sessionUuid,
              });

              addOrUpdateMessage({ sessionUuid, message });
            },
            "message.part.created": (parsedEvent) => {
              const messageOpencodeId = parsedEvent.properties.part.messageID;
              const message = getMessageByOpencodeId({
                sessionUuid,
                messageOpencodeId,
              });
              if (!message) {
                return;
              }

              const part = transformUpstreamToOpencodeMessagePart({
                upstreamPart: parsedEvent.properties.part,
                opencodeMessageUuid: message.id,
              });

              addOrUpdateMessagePart({ sessionUuid, messageOpencodeId, part });
            },
            "message.part.added": (parsedEvent) => {
              const messageOpencodeId = parsedEvent.properties.part.messageID;
              const message = getMessageByOpencodeId({
                sessionUuid,
                messageOpencodeId,
              });
              if (!message) {
                return;
              }

              const part = transformUpstreamToOpencodeMessagePart({
                upstreamPart: parsedEvent.properties.part,
                opencodeMessageUuid: message.id,
              });

              addOrUpdateMessagePart({ sessionUuid, messageOpencodeId, part });
            },
            "message.part.updated": (parsedEvent) => {
              const messageOpencodeId = parsedEvent.properties.part.messageID;
              const message = getMessageByOpencodeId({
                sessionUuid,
                messageOpencodeId,
              });
              if (!message) {
                return;
              }

              const part = transformUpstreamToOpencodeMessagePart({
                upstreamPart: parsedEvent.properties.part,
                opencodeMessageUuid: message.id,
              });

              handleToolNavigationIfNeeded({
                part,
                sandboxNavigate,
                openTab,
              });
              addOrUpdateMessagePart({ sessionUuid, messageOpencodeId, part });
            },
          });
        }
      }
    },
    [
      addEvent,
      addOrUpdateMessage,
      addOrUpdateMessagePart,
      applyTextDelta,
      getMessageByOpencodeId,
      getSessionUuidByAgentSessionId,
      sandboxNavigate,
      setQuestionId,
      registerSubagentAgentSessionId,
      registerSubagentSession,
      completeSubagentSession,
      isSubagentSession,
      setToolCallSubagentSession,
      findRunningTaskToolCallId,
      hasActiveSubagents,
      closeParentSession,
      user?.id,
      openTab,
    ],
  );

  const { state: agentState } = useReploAgent({ onEvent: handleEvent });

  // NOTE (Noah, 2026-02-22): Now keyed by sessionUuid instead of agentSessionId
  const previewUrl = currentSessionUuid
    ? agentState.streamingSessionState?.[currentSessionUuid]?.previewUrl
    : undefined;

  useEffect(() => {
    setChatPreviewUrl(previewUrl ?? null);
  }, [previewUrl, setChatPreviewUrl]);
}
